import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import type { WorkflowConfig, WorkflowFieldDef } from '../../lib/workflows/config';
import type { RuleOp, RuleRef, WorkflowRule } from '../../lib/workflows/rules';
import { runRules } from '../../lib/workflows/rules';
import { ruleText, whenText, refKind } from '../../lib/workflows/ruleFormat';
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from '../../lib/workflows/dateFormat';
import { canonicalEbtsRef, formatEbtsRef, tryParseEbtsRef } from '../../lib/workflows/ebtsRef';
import { ALL_FGPS, FGP_LABEL, requiredPositions } from '../../lib/workflows/fingerprintPositions';

type AdminWorkflow = WorkflowConfig & { source: 'stored' | 'default'; builtin: boolean };

/** What the specification says about one mappable field — served by GET /admin/specs/fields. */
interface SpecFieldValue { code: string; label: string }
interface SpecField {
  ref: string; record: number; field: number; mnemonic: string;
  description?: string; longDescription?: string; example?: string;
  characterType: string; minLength: number; maxLength: number; characterSet?: string;
  condition?: string; values?: SpecFieldValue[]; valuesMandatory?: boolean;
  inputType: 'text' | 'number' | 'date' | 'select' | 'textarea';
}

const BLANK: WorkflowConfig = {
  captureType: '', label: '', tag: '', specification: 'fbi_ebts_8', tot: 'FAUF', ori: '', retention: 'Y',
  zeroPaddedIdc: true, requiresDeviceService: true, tabs: ['demographics', 'fingerprints'],
  terminalAction: 'download', billing: 'device-license-or-metered',
  staticType1Fields: {}, staticType2Fields: {}, fieldMap: {}, tcnTemplate: '{ori}-{datetime}-EFTC-{rand:5}',
  postRules: { setName: true, dprFromDat: true }, fields: [], enabled: true,
};

export function Workflows() {
  const isAdmin = useIsSiteAdmin();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['admin-workflows'], queryFn: () => api.get<AdminWorkflow[]>('/admin/workflows'), enabled: isAdmin });

  if (!isAdmin) return <div className="panel empty"><h3>Restricted</h3><p>Workflow configuration is available to Lakota platform staff only.</p></div>;

  const rows = list.data ?? [];

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Transaction workflows</h1>
          <div className="sub">Configure the preprocessing (fields, EBTS mapping, TCN) for each transaction type — or add new ones.</div>
        </div>
        <Link className="btn btn-primary" to={workflowPath('new')}>+ New workflow</Link>
      </div>

      {/*
        * A list that opens a page, not a side-by-side split. The editor is a long form — fields, static
        * records, TCN, layout — and squeezing it beside a table left it in a column too narrow to read while
        * the list took space that only ever showed five rows. Same shape as Customers -> Customer detail, so
        * the two admin consoles behave the same way.
        */}
      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        {list.isLoading ? <p className="loading-screen">// loading…</p> : rows.length === 0 ? (
          <p className="cart-note" style={{ textAlign: 'left', padding: 16 }}>// no workflows configured</p>
        ) : (
          <table className="tx-table">
            <thead><tr><th>Workflow</th><th>Type</th><th>Specification</th><th>Fields</th><th>Source</th><th></th></tr></thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.captureType} className="row-click" onClick={() => navigate(workflowPath(w.captureType))}>
                  <td>
                    {w.label || w.captureType}
                    <div className="acct-code">{w.captureType}{!w.enabled && ' · disabled'}</div>
                  </td>
                  <td>{w.tot}</td>
                  <td className="mono">{w.specification}</td>
                  <td>{(w.fields ?? []).length}</td>
                  <td><span className={`status-dot ${w.source === 'stored' ? 'ok' : 'muted'}`}>{w.source === 'stored' ? 'Customized' : 'Default'}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="track-link" to={workflowPath(w.captureType)} onClick={(e) => e.stopPropagation()}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Detail route for one workflow. `new` opens a blank one. */
export const workflowPath = (captureType: string) => `/app/admin/workflows/${encodeURIComponent(captureType)}`;

export function WorkflowDetailPage() {
  const { captureType = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useIsSiteAdmin();
  const list = useQuery({ queryKey: ['admin-workflows'], queryFn: () => api.get<AdminWorkflow[]>('/admin/workflows'), enabled: isAdmin });

  if (!isAdmin) return <div className="panel empty"><h3>Restricted</h3><p>Workflow configuration is available to Lakota platform staff only.</p></div>;
  if (list.isLoading) return <p className="loading-screen">// loading workflow…</p>;

  const rows = list.data ?? [];
  const isNew = captureType === 'new';
  const found = rows.find((w) => w.captureType === captureType);
  const initial = isNew ? { ...BLANK } : found;

  if (!initial) {
    return (
      <div className="panel empty">
        <h3>Unknown workflow</h3>
        <p><Link to="/app/admin/workflows" className="track-link">← Transaction workflows</Link></p>
      </div>
    );
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>{isNew ? 'New workflow' : initial.label || initial.captureType}</h1>
          <div className="sub">
            <Link to="/app/admin/workflows" className="track-link">← Transaction workflows</Link>
            {!isNew && <>{' · '}<span className="mono">{initial.captureType}</span></>}
          </div>
        </div>
      </div>
      <WorkflowEditor
        key={isNew ? '__new__' : captureType}
        initial={initial}
        isNew={isNew}
        peers={rows}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['admin-workflows'] });
          qc.invalidateQueries({ queryKey: ['workflows'] });
          // Back to the list: saving or deleting is the end of the task, and staying on a page whose
          // subject may no longer exist is how a deleted workflow looks like a broken screen.
          navigate('/app/admin/workflows');
        }}
      />
    </div>
  );
}

/**
 * The editor's tabs, in the order an admin works through one: what it IS, the constants it always writes,
 * the form the operator fills, what it captures, then the processing applied on the way out.
 */
const WF_TABS = [
  ['info', 'Info'],
  ['static', 'Static fields'],
  ['demographics', 'Demographics'],
  ['biometrics', 'Biometrics'],
  ['rules', 'Rules'],
] as const;
type WfTab = (typeof WF_TABS)[number][0];

function WorkflowEditor({ initial, isNew, peers, onDone }: {
  initial: WorkflowConfig; isNew: boolean; peers: AdminWorkflow[]; onDone: () => void;
}) {
  const [d, setD] = useState<WorkflowConfig>(structuredClone(initial));
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof WorkflowConfig>(k: K, v: WorkflowConfig[K]) => setD((p) => ({ ...p, [k]: v }));

  const save = useMutation({
    mutationFn: () => api.put<WorkflowConfig>(`/admin/workflows/${encodeURIComponent(d.captureType)}`, d),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Save failed'),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/admin/workflows/${encodeURIComponent(d.captureType)}`),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  /*
   * What the workflow's own specification says about each field. Re-read when the specification or the TOT
   * changes, because the mandatory/optional answer is per transaction type.
   */
  /** The specification keys this build carries. A workflow's `specification` must be one of them. */
  const specs = useQuery({
    queryKey: ['specifications'],
    queryFn: () => api.get<{ specifications: { id: string; tots: string[]; files: string[] }[] }>('/admin/specs'),
    staleTime: 600_000,
  });

  const specFields = useQuery({
    queryKey: ['spec-fields', d.specification, d.tot],
    queryFn: () => api.get<{ fields: SpecField[] }>(
      `/admin/specs/fields?spec=${encodeURIComponent(d.specification)}&tot=${encodeURIComponent(d.tot)}`,
    ),
    enabled: Boolean(d.specification),
    staleTime: 600_000,
  });

  const toggleTab = (t: string) => set('tabs', d.tabs.includes(t) ? d.tabs.filter((x) => x !== t) : [...d.tabs, t]);
  /*
   * Only workflows that can actually entitle this one: enabled, billed the same way, and not already
   * delegating themselves. The server enforces the same rule — this keeps an admin from picking something
   * it will refuse.
   */
  const [tab, setTab] = useState<WfTab>('info');
  /** A card conversion captures uploaded images, not impressions, so it has no biometrics to configure. */
  const isConvType = d.captureType.startsWith('conv');

  const billsAsOptions = peers.filter(
    (w) => w.captureType !== d.captureType && w.enabled && w.billing === d.billing && !w.billsAs,
  );

  return (
    <div className="stack">
      {/*
        * The name, the on/off switch and the tab strip stay put; everything else belongs to a tab.
        *
        * The page had grown to six stacked panels covering identity, TCN, biometrics, rules, static fields
        * and the demographic form — related only in that they are all this workflow. Splitting them means
        * an admin editing the form fields is not scrolling past the TCN template to reach them.
        *
        * Every tab reads and writes the SAME `d`, so switching is only a change of view: nothing is saved,
        * discarded or reset on the way, and one Save at the bottom still commits the lot.
        */}
      <div className="panel wf-head">
        <div className="panel-head"><h3 style={{ margin: 0 }}>{isNew ? 'New workflow' : d.label || d.captureType}</h3>
          <label className="checkbox"><input type="checkbox" checked={d.enabled} onChange={(e) => set('enabled', e.target.checked)} /><span>Enabled</span></label>
        </div>
        <div className="seg wf-tabs">
          {WF_TABS.filter(([id]) => id !== 'biometrics' || !isConvType).map(([id, label]) => (
            <button key={id} type="button" className={`seg-btn${tab === id ? ' is-active' : ''}`}
              onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      </div>

      {tab === 'info' && <>
      <div className="panel">
        <div className="demo-grid">
          <Field label="Capture type (id)">
            <input className="input" value={d.captureType} disabled={!isNew}
              onChange={(e) => set('captureType', e.target.value.trim())} placeholder="e.g. atf4 or a new id" />
            {/*
              * Fixed after creation on purpose: the id is stamped onto every transaction of this type and
              * is the key the configuration is stored under. Renaming it would strand the history rather
              * than move it. A disabled box with no explanation reads as a bug, so it says so.
              */}
            <span className="field-help">
              {isNew
                ? 'Lower-case id used in URLs and stamped on every transaction. Cannot be changed later.'
                : 'Fixed — this id is stamped on existing transactions. To rename, create a new workflow and disable this one.'}
            </span>
          </Field>
          <Field label="Label"><input className="input" value={d.label} onChange={(e) => set('label', e.target.value)} /></Field>
          <Field label="Tag"><input className="input" value={d.tag} onChange={(e) => set('tag', e.target.value)} /></Field>
          {/*
            * A key from the registry, not free text. It was an open input, so a typo pointed the workflow at a
            * specification that does not exist — and nothing said so until a capture failed to validate. The
            * list comes from the API, so adding a specification makes it selectable without a code change here.
            */}
          <Field label="Specification">
            <select className="select" value={d.specification} onChange={(e) => set('specification', e.target.value)}>
              {/* A workflow already naming a key the build no longer carries keeps it listed, so saving cannot
                  silently repoint it at something else. */}
              {d.specification && !(specs.data?.specifications ?? []).some((x) => x.id === d.specification) && (
                <option value={d.specification}>{d.specification} (not in this build)</option>
              )}
              {(specs.data?.specifications ?? []).map((x) => (
                <option key={x.id} value={x.id}>{x.id} — {x.tots.length} transaction type{x.tots.length === 1 ? '' : 's'}</option>
              ))}
            </select>
            {specs.isLoading && <span className="field-help">reading the specifications…</span>}
          </Field>
          <Field label="Type of transaction (TOT)"><input className="input" value={d.tot} onChange={(e) => set('tot', e.target.value)} /></Field>
          <Field label="ORI"><input className="input" value={d.ori} onChange={(e) => set('ori', e.target.value)} /></Field>
          <Field label="Retention">
            <select className="select" value={d.retention} onChange={(e) => set('retention', e.target.value as WorkflowConfig['retention'])}>
              <option value="Y">Y</option><option value="N">N</option><option value="">(none)</option>
            </select>
          </Field>
          <Field label="Terminal action">
            <select className="select" value={d.terminalAction} onChange={(e) => set('terminalAction', e.target.value as WorkflowConfig['terminalAction'])}>
              <option value="download">download (.eft)</option><option value="submit-fbi">submit to FBI</option><option value="submit-conversion">card conversion</option>
            </select>
          </Field>
          <Field label="Billing">
            <select className="select" value={d.billing} onChange={(e) => set('billing', e.target.value as WorkflowConfig['billing'])}>
              <option value="device-license-or-metered">device-license or metered</option><option value="credit-or-metered">credit or metered</option>
            </select>
          </Field>
          {/*
            * A plan entitles a capture type by name, and no plan in the catalog names an id that did not
            * exist when it was written — so a new workflow is refused for want of a subscription unless it
            * bills against one that is sold. Blank means "sold under its own id".
            */}
          <Field label="Bills against">
            <select className="select" value={d.billsAs ?? ''}
              onChange={(e) => set('billsAs', e.target.value || undefined)}>
              <option value="">its own plan (a plan must be scoped to this id)</option>
              {billsAsOptions.map((w) => (
                <option key={w.captureType} value={w.captureType}>{w.label || w.captureType} ({w.captureType})</option>
              ))}
            </select>
            <span className="field-help">
              {d.billsAs
                ? `Uses the customer's ${billsAsOptions.find((w) => w.captureType === d.billsAs)?.label ?? d.billsAs} plan — no separate purchase, and nothing to change in Stripe.`
                : 'Customers need a plan scoped to this capture type. Pick an existing one to bill against instead.'}
            </span>
          </Field>
        </div>
        <div className="inline-form" style={{ marginTop: 12 }}>
          <label className="checkbox"><input type="checkbox" checked={d.zeroPaddedIdc} onChange={(e) => set('zeroPaddedIdc', e.target.checked)} /><span>Zero-padded IDC</span></label>
          <label className="checkbox"><input type="checkbox" checked={d.requiresDeviceService} onChange={(e) => set('requiresDeviceService', e.target.checked)} /><span>Requires Device Service</span></label>
          {/* How DOCE and SWFT have always behaved, now a setting rather than two hardcoded lists. */}
          <label className="checkbox">
            <input type="checkbox" checked={Boolean(d.requiresAuthorization)}
              onChange={(e) => set('requiresAuthorization', e.target.checked)} />
            <span>Requires pre-authorization — hidden until enabled on an account</span>
          </label>
          {['demographics', 'fingerprints', 'documents'].map((t) => (
            <label key={t} className="checkbox"><input type="checkbox" checked={d.tabs.includes(t)} onChange={() => toggleTab(t)} /><span>{t}</span></label>
          ))}
        </div>
      </div>

      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>TCN template</div>
        <input className="input" style={{ width: '100%' }} value={d.tcnTemplate} onChange={(e) => set('tcnTemplate', e.target.value)} />
        <p className="cart-note" style={{ textAlign: 'left' }}>
          // Tokens: {'{ori} {tot} {spec} {date} {datetime} {rand:N} {fill:TOTAL} {field:NAME|alnum} {?NAME:suffix}'}
        </p>
      </div>
      </>}

      {/*
        * What this workflow collects. The legacy "Post rules" flags that used to sit here are gone: they
        * were a second way to do what a post-processing rule describes, and a stored configuration folds
        * them into its rules as it loads.
        */}
      {tab === 'biometrics' && !isConvType && (
        <div className="panel">
          <div className="section-title" style={{ marginTop: 0 }}>Fingerprints collected</div>
          <p className="field-help" style={{ margin: '0 0 4px' }}>
            The impressions an operator must take. Each one has to be captured, or marked amputated or
            unprintable, before the file can be built. Leave all fourteen ticked for a full ten-print.
          </p>
          <div className="wf-fgp">
            {ALL_FGPS.map((fgp) => {
              const chosen = requiredPositions(d.biometrics?.fingerprintPositions);
              const on = chosen.includes(fgp);
              return (
                <label className="checkbox" key={fgp}>
                  <input type="checkbox" checked={on}
                    onChange={(e) => {
                      const next = e.target.checked ? [...chosen, fgp] : chosen.filter((x) => x !== fgp);
                      /*
                       * Unticking the last one would mean "collect nothing", which builds an EFT with no
                       * impressions. Treated as all fourteen everywhere else, so the box simply refuses.
                       */
                      if (!next.length) return;
                      set('biometrics', { ...d.biometrics, fingerprintPositions: next.sort((a, b) => a - b) });
                    }} />
                  <span>{fgp}. {FGP_LABEL[fgp]}</span>
                </label>
              );
            })}
          </div>
          <div className="wf-fgp-actions">
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => set('biometrics', { ...d.biometrics, fingerprintPositions: [...ALL_FGPS] })}>All fourteen</button>
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => set('biometrics', { ...d.biometrics, fingerprintPositions: [11, 12] })}>Flat thumbs only</button>
          </div>
        </div>
      )}

      {tab === 'rules' && (
      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Post-processing rules</div>
        <p className="field-help" style={{ margin: '0 0 10px' }}>
          {/*
            * The checkboxes above are the four that were hardcoded. Anything new belongs here: a rule can do
            * everything they do, and adding one is a configuration change rather than a deploy.
            */}
          Read a value, transform it, write it to a form field or an EBTS field. Rules run in order and can
          read what an earlier rule wrote — that is how a value the operator never types gets computed.
        </p>
        <RulesEditor
          rules={d.rules ?? []}
          onChange={(r) => set('rules', r.length ? r : undefined)}
          fieldNames={(d.fields ?? []).map((f) => f.name).filter(Boolean)}
        />
      </div>
      )}

      {tab === 'static' && (
      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Static fields</div>
        <p className="field-help" style={{ margin: '0 0 8px' }}>
          {/*
            * One list instead of a Type-1 table and a Type-2 table. The record is part of the reference, so
            * there is nothing to decide before adding a constant, and the field is chosen from the
            * specification rather than typed as a number.
            */}
          Values this workflow writes on every transaction. Pick the field; the record comes with it.
        </p>
        <StaticFieldsEditor
          statics={d.staticFields ?? mergeStatics(d)}
          onChange={(r) => set('staticFields', r)}
          specFields={specFields.data?.fields ?? []}
          specLoading={specFields.isLoading}
        />
      </div>
      )}

      {tab === 'demographics' && (
      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Demographic form fields</div>
        <FieldsEditor fields={d.fields} onChange={(f) => set('fields', f)} fieldMap={d.fieldMap}
          onFieldMapChange={(m) => set('fieldMap', m)}
          specFields={specFields.data?.fields ?? []} specLoading={specFields.isLoading}
          specError={specFields.error ? (specFields.error instanceof ApiError ? specFields.error.message : 'Could not read the specification') : null} />
      </div>
      )}

      {err && <p className="cart-error">{err}</p>}
      <div className="flow-actions">
        {!isNew && !initial.builtin && (
          <button className="btn btn-ghost cancel-btn" onClick={() => remove.mutate()} disabled={remove.isPending}>Delete workflow</button>
        )}
        {!isNew && initial.builtin && initial.source === 'stored' && (
          <button className="btn btn-ghost" onClick={() => remove.mutate()} disabled={remove.isPending}>Revert to default</button>
        )}
        <button className="btn btn-primary" onClick={() => { setErr(null); if (!d.captureType) { setErr('Capture type id is required'); return; } save.mutate(); }} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save workflow'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="field"><span className="field-label">{label}</span>{children}</label>;
}

/**
 * Editable demographic field list.
 *
 * One collapsed row per field — form id, input type, EBTS mapping — expanded to edit the rest. It was a
 * single wide row of controls per field, which made a fifteen-field workflow a wall of inputs where the
 * thing you were looking for was whichever box happened to be third from the left.
 *
 * Dragged to reorder, because the order here IS the order on the form. Only the handle drags: a row that
 * drags from anywhere cannot have its text selected. The arrows do the same job from the keyboard, which a
 * native HTML5 drag cannot.
 */
/**
 * Canonical spelling for display, or the value untouched when it cannot be parsed.
 *
 * The editor matched saved references by exact string against a specification indexed as `2.024`, so the
 * legacy form (`24`, meaning Type-2 field 24) matched nothing and reported a field as absent from a
 * specification that defines it. `canonicalEbtsRef` throws on anything unreadable, which a render must
 * not do — an unreadable mapping should be shown as-is so it can be corrected, not crash the page.
 */
function displayRef(raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return v;
  try {
    return canonicalEbtsRef(v);
  } catch {
    return v;
  }
}

function FieldsEditor({ fields, onChange, fieldMap, onFieldMapChange, specFields, specLoading, specError }: {
  fields: WorkflowFieldDef[];
  onChange: (f: WorkflowFieldDef[]) => void;
  /** The stored mapping, so a configuration written before `ebtsField` still shows its field. */
  fieldMap: Record<string, string>;
  onFieldMapChange: (m: Record<string, string>) => void;
  specFields: SpecField[];
  specLoading: boolean;
  specError: string | null;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  const byRef = new Map(specFields.map((sf) => [sf.ref, sf]));
  const upd = (i: number, patch: Partial<WorkflowFieldDef>) => onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  /** Drop a key rather than storing an empty or default value, so a config says only what it means. */
  const clear = (i: number, key: keyof WorkflowFieldDef, value?: unknown) =>
    onChange(fields.map((f, idx) => {
      if (idx !== i) return f;
      const { [key]: _drop, ...rest } = f;
      return (value ? { ...rest, [key]: value } : rest) as WorkflowFieldDef;
    }));

  /**
   * The field whose EBTS reference is being chosen right now.
   *
   * Local, not persisted: "I want this mapped but have not said to what" is a state of the editor, not of
   * the workflow. Storing it would mean writing a half-made mapping to a configuration customers run.
   */
  const [picking, setPicking] = useState<string | null>(null);
  /*
   * Canonicalised, because a saved mapping may be in any of the three forms these configurations have
   * used — `24`, `2.24`, `2.024`. The specification indexes fields as `2.024`, so without this the older
   * forms match nothing and the picker reports a field as absent from a specification that defines it.
   */
  const refOf = (f: WorkflowFieldDef): string => displayRef(f.ebtsField ?? fieldMap[f.name] ?? '');
  /*
   * A reference names a field AND a position inside it — `2.073.1.2` is field 73, subfield 1, item 2. The
   * picker lists whole fields, so it has to match on the field alone; the position gets its own controls.
   * Without this split a mapping like SWFT's SOI could not be shown at all: the select had no matching
   * option and fell through to "(not in this specification)".
   */
  const baseRefOf = (f: WorkflowFieldDef): string => {
    const p = tryParseEbtsRef(refOf(f));
    return p ? formatEbtsRef({ record: p.record, field: p.field }) : refOf(f);
  };
  const posOf = (f: WorkflowFieldDef): { subfield: number; item: number } => {
    const p = tryParseEbtsRef(refOf(f));
    return { subfield: p?.subfield ?? 1, item: p?.item ?? 1 };
  };
  /** Move a mapping to another subfield/item of the SAME field. */
  const setPos = (i: number, patch: { subfield?: number; item?: number }) => {
    const f = fields[i];
    if (!f) return;
    const p = tryParseEbtsRef(refOf(f));
    if (!p) return;
    const subfield = Math.max(1, patch.subfield ?? p.subfield);
    const item = Math.max(1, patch.item ?? p.item);
    const ref = formatEbtsRef({ record: p.record, field: p.field, subfield, item });
    if (f.name) onFieldMapChange({ ...fieldMap, [f.name]: ref });
    upd(i, { ebtsField: ref });
  };
  const specOf = (f: WorkflowFieldDef): SpecField | undefined => byRef.get(baseRefOf(f));
  const offered = (f: WorkflowFieldDef, sf: SpecField): Set<string> =>
    new Set((f.options ?? sf.values ?? []).map((o) => ('value' in o ? o.value : (o as { code: string }).code)));

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= fields.length) return;
    const next = [...fields];
    const [item] = next.splice(from, 1);
    if (item) next.splice(to, 0, item);
    onChange(next);
    setOpen(open === from ? to : open);
  };

  /**
   * Adopt a specification field: the reference, the input it implies, and every value it permits.
   *
   * The full list is the default because the alternative is hand-typing it, and hand-typing is how Sex came
   * to offer two of the seven codes EBTS defines. Narrow it afterwards — a workflow may offer fewer than the
   * specification allows, never more, since a value outside an `mmap` is a violation the FBI rejects.
   */
  const adopt = (i: number, ref: string) => {
    const sf = byRef.get(ref);
    const name = fields[i]?.name;
    /*
     * `fieldMap` is what buildCustomEft actually writes from — `ebtsField` on its own decorates the editor
     * and changes nothing in the produced EFT. They have to move together or the switch is a placebo.
     */
    /*
     * Keep the subfield/item the mapping already carried: choosing a different FIELD is not a statement
     * about position, and silently resetting SWFT's SOI from item 2 back to item 1 would put two values
     * in the same place.
     */
    const cur = name ? tryParseEbtsRef(refOf(fields[i]!)) : undefined;
    const positioned =
      ref && cur && (cur.subfield !== 1 || cur.item !== 1)
        ? (() => {
            const p = tryParseEbtsRef(ref);
            return p ? formatEbtsRef({ record: p.record, field: p.field, subfield: cur.subfield, item: cur.item }) : ref;
          })()
        : ref;
    if (name) {
      const next = { ...fieldMap };
      if (positioned) next[name] = positioned;
      else delete next[name];
      onFieldMapChange(next);
    }
    onChange(fields.map((f, idx) => {
      if (idx !== i) return f;
      const { ebtsField: _e, options: _o, lookup: _l, ...rest } = f;
      if (!ref || !sf) return rest as WorkflowFieldDef;
      return {
        ...rest,
        ebtsField: positioned,
        type: sf.inputType,
        ...(sf.values?.length ? { options: sf.values.map((v) => ({ value: v.code, label: v.label })) } : {}),
        ...(sf.condition === 'M' ? { required: true } : {}),
      };
    }));
  };

  /**
   * Turn EBTS mapping on or off for a field.
   *
   * Both directions were broken. ON did nothing at all — it returned immediately, waiting for a picker
   * that only renders once a reference exists, so the box could never be ticked. OFF cleared `ebtsField`
   * but `refOf` then fell back to `fieldMap[name]`, so every standard demographic sprang straight back to
   * ticked; and because the builder reads `fieldMap`, the field kept being written to the EBTS regardless
   * of what the switch showed.
   *
   * ON is therefore local intent — it reveals the picker; `adopt` commits the choice. OFF removes the
   * reference, the values it supplied, AND the fieldMap entry, which is the one that decides output.
   */
  const setMapped = (i: number, on: boolean) => {
    const name = fields[i]?.name;
    if (on) {
      setPicking(name ?? null);
      return;
    }
    setPicking(null);
    if (name && fieldMap[name] !== undefined) {
      const next = { ...fieldMap };
      delete next[name];
      onFieldMapChange(next);
    }
    onChange(fields.map((f, idx) => {
      if (idx !== i) return f;
      const { ebtsField: _e, options: _o, lookup: _l, ...rest } = f;
      return rest as WorkflowFieldDef;
    }));
  };

  const toggleValue = (i: number, code: string) => {
    const f = fields[i];
    const sf = f && specOf(f);
    if (!f || !sf?.values) return;
    const chosen = offered(f, sf);
    if (chosen.has(code)) {
      if (chosen.size <= 1) return; // a select with nothing to choose from is broken, not narrow
      chosen.delete(code);
    } else {
      chosen.add(code);
    }
    const next = sf.values.filter((v) => chosen.has(v.code)).map((v) => ({ value: v.code, label: v.label }));
    upd(i, { options: next, ...(f.ebtsField ? {} : { ebtsField: sf.ref }) });
  };

  /** Values for a select that is NOT EBTS-mapped — the workflow's own list, edited by hand. */
  const setOwnOption = (i: number, oi: number, patch: { value?: string; label?: string }) => {
    const opts = [...(fields[i]?.options ?? [])];
    const cur = opts[oi];
    if (!cur) return;
    opts[oi] = { value: patch.value ?? cur.value, label: patch.label ?? cur.label };
    upd(i, { options: opts });
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      {fields.map((f, i) => {
        const sf = specOf(f);
        const mapped = Boolean(refOf(f)) || picking === f.name;
        const isOpen = open === i;
        return (
          <div
            className={`wf-row${dragging === i ? ' is-dragging' : ''}${over === i && dragging !== null && dragging !== i ? ' is-over' : ''}`}
            key={i}
            onDragOver={(e) => { e.preventDefault(); setOver(i); }}
            onDrop={(e) => { e.preventDefault(); if (dragging !== null) move(dragging, i); setDragging(null); setOver(null); }}
          >
            {/* Collapsed: the three things that identify a field, and nothing else. */}
            <div className="wf-head">
              <span className="wf-drag" draggable title="Drag to reorder" aria-hidden="true"
                onDragStart={() => setDragging(i)}
                onDragEnd={() => { setDragging(null); setOver(null); }}>⋮⋮</span>
              <button type="button" className="wf-disclose" aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : i)}>
                <span className="wf-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                <span className="wf-id mono">{f.name || <em>unnamed</em>}</span>
                <span className="wf-type">{f.type}</span>
                {mapped
                  ? <span className="wf-ebts mono" title={sf?.description ?? ''}>{refOf(f)}{sf ? ` ${sf.mnemonic}` : ''}</span>
                  : <span className="wf-ebts wf-unmapped">not mapped</span>}
                {f.required && <span className="wf-req" title="Required">required</span>}
                {f.defaultValue && <span className="wf-default mono" title="Default value">= {f.defaultValue}</span>}
                {f.type === 'select' && !(f.options ?? []).length && !mapped && (
                  <span className="wf-warnpill" title="This dropdown has no values, so it will be empty on the form">no values</span>
                )}
              </button>
              <button className="btn btn-ghost btn-sm wf-move" disabled={i === 0} title="Move up"
                aria-label={`Move ${f.label || f.name || 'field'} up`} onClick={() => move(i, i - 1)}>↑</button>
              <button className="btn btn-ghost btn-sm wf-move" disabled={i === fields.length - 1} title="Move down"
                aria-label={`Move ${f.label || f.name || 'field'} down`} onClick={() => move(i, i + 1)}>↓</button>
              <button className="btn btn-ghost btn-sm" title="Remove field"
                onClick={() => { onChange(fields.filter((_, idx) => idx !== i)); setOpen(null); }}>✕</button>
            </div>

            {isOpen && (
              <div className="wf-body">
                <div className="wf-grid">
                  <Field label="Form id"><input className="input mono" value={f.name}
                    onChange={(e) => upd(i, { name: e.target.value.trim() })} placeholder="e.g. hairColor" /></Field>
                  <Field label="Label"><input className="input" value={f.label}
                    onChange={(e) => upd(i, { label: e.target.value })} placeholder="Hair color" /></Field>
                  <Field label="Input type">
                    {/*
                      * NOT locked when the field is mapped, which it used to be. The specification describes
                      * bytes, so its answer is a sensible default and not the last word: an address mapped to
                      * 2.041 was pinned to a single-line input, and a single-line input cannot produce the
                      * newline that field is explicitly allowed to carry.
                      */}
                    <select className="select" value={f.type}
                      onChange={(e) => upd(i, { type: e.target.value as WorkflowFieldDef['type'] })}>
                      <option value="text">text</option><option value="textarea">textarea (multi-line)</option>
                      <option value="date">date</option><option value="select">select</option>
                      <option value="number">number</option>
                    </select>
                    {sf && (
                      <span className="field-help">
                        {sf.inputType === f.type
                          ? `The specification suggests ${sf.inputType}.`
                          : `The specification suggests ${sf.inputType} — you have chosen ${f.type}.`}
                        {sf.characterSet && /PRINTCTRL/i.test(sf.characterSet) && ' This field permits line breaks.'}
                      </span>
                    )}
                  </Field>
                </div>

                <div className="wf-inline">
                  <label className="checkbox"><input type="checkbox" checked={!!f.required}
                    onChange={(e) => upd(i, { required: e.target.checked })} /><span>Required</span></label>
                  <label className="wf-span" title="Columns this field spans, of three">
                    <span>cols</span>
                    <select className="select" value={String(f.colSpan ?? 1)}
                      onChange={(e) => clear(i, 'colSpan', Number(e.target.value) > 1 ? Number(e.target.value) : undefined)}>
                      <option value="1">1</option><option value="2">2</option><option value="3">3</option>
                    </select>
                  </label>
                  <label className="wf-span" title="Rows this field spans — for a taller textarea">
                    <span>rows</span>
                    <select className="select" value={String(f.rowSpan ?? 1)}
                      onChange={(e) => clear(i, 'rowSpan', Number(e.target.value) > 1 ? Number(e.target.value) : undefined)}>
                      <option value="1">1</option><option value="2">2</option><option value="3">3</option>
                    </select>
                  </label>
                  <label className="checkbox" title="Start a new row before this field">
                    <input type="checkbox" checked={!!f.breakBefore}
                      onChange={(e) => clear(i, 'breakBefore', e.target.checked || undefined)} /><span>new row</span>
                  </label>
                </div>

                {/*
                  * Offered for a REQUIRED field, since that is where it earns its keep — the operator has to
                  * fill it either way. Also shown when one is already set on a field that has since been made
                  * optional, so a default cannot go on applying from a control nobody can see.
                  */}
                {(f.required || f.defaultValue) && (
                  <div className="wf-grid">
                    <Field label="Default value">
                      {f.type === 'select' ? (
                        <select className="select" value={f.defaultValue ?? ''}
                          onChange={(e) => clear(i, 'defaultValue', e.target.value || undefined)}>
                          <option value="">— none —</option>
                          {(f.options ?? []).map((o) => (
                            <option key={o.value} value={o.value}>{o.label || o.value}</option>
                          ))}
                        </select>
                      ) : (
                        <input className="input" type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                          value={f.defaultValue ?? ''} placeholder="left blank if not set"
                          onChange={(e) => clear(i, 'defaultValue', e.target.value || undefined)} />
                      )}
                      <span className="field-help">
                        Fills the field when a capture starts. A saved draft, an imported file and anything the
                        operator types all take precedence.
                        {!f.required && f.defaultValue && ' This field is no longer required, but its default still applies.'}
                      </span>
                    </Field>
                  </div>
                )}

                {/*
                  * A date input always yields YYYY-MM-DD; the EBTS field almost always wants CCYYMMDD.
                  * Naming the format here is what makes that conversion happen — it used to be hardcoded
                  * for date of birth alone, so every other mapped date went out with its separators.
                  */}
                {f.type === 'date' && (
                  <Field label="Written to the EBTS as">
                    <select className="select" value={f.dateFormat ?? DEFAULT_DATE_FORMAT}
                      onChange={(e) => upd(i, { dateFormat: e.target.value })}>
                      {DATE_FORMATS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                    <span className="field-help">
                      The operator always sees a date picker; this is only how the value is written out.
                    </span>
                  </Field>
                )}

                <div className="wf-grid">
                  <Field label="Placeholder"><input className="input" value={f.placeholder ?? ''}
                    onChange={(e) => clear(i, 'placeholder', e.target.value || undefined)} /></Field>
                  <Field label="Help text (shown under the control)"><input className="input" value={f.help ?? ''}
                    onChange={(e) => clear(i, 'help', e.target.value || undefined)} /></Field>
                </div>

                {/*
                  * Not every field belongs in the EBTS. An operator note, or a value the workflow only uses to
                  * build the TCN, has no field to be written to — so mapping is a choice rather than the
                  * assumption it used to be.
                  */}
                <label className="checkbox wf-mapswitch">
                  <input type="checkbox" checked={mapped} onChange={(e) => setMapped(i, e.target.checked)} />
                  <span>Write this field to the EBTS</span>
                </label>

                {/* Checked, or being chosen: the picker and what the specification says about the choice. */}
                {mapped && (
                  <div className="wf-field-spec">
                    <label className="wf-span" title="The EBTS field this input is written to">
                      <span>EBTS</span>
                      <select className="select" style={{ maxWidth: 340 }} value={baseRefOf(f)}
                        onChange={(e) => adopt(i, e.target.value)}>
                        <option value="">— choose a field —</option>
                        {baseRefOf(f) && !byRef.has(baseRefOf(f)) && (
                          <option value={baseRefOf(f)}>{baseRefOf(f)} (not in this specification)</option>
                        )}
                        {specFields.map((s) => (
                          <option key={s.ref} value={s.ref}>
                            {s.ref} {s.mnemonic}{s.description ? ` — ${s.description}` : ''}{s.condition === 'M' ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/*
                      * Where inside the field the value goes.
                      *
                      * Several fields hold a group of related values, each in its own subfield or item —
                      * SWFT writes SON, SOI and IPAC into 2.073 subfields 1, 2 and 3, and the scanner make,
                      * model and serial into 2.067 items 1, 2 and 3. The picker lists whole fields, so
                      * without these the editor could neither show such a mapping nor create one: a
                      * positioned reference matched no option and read as "not in this specification".
                      *
                      * Both default to 1, which is the plain case and the form a reference drops.
                      */}
                    {baseRefOf(f) && (
                      <div className="wf-pos">
                        <label title="Subfield within the field — 1 unless the field holds a group">
                          <span>Subfield</span>
                          <input className="input wf-pos-num" type="number" min={1} value={posOf(f).subfield}
                            onChange={(e) => setPos(i, { subfield: Number(e.target.value) })} />
                        </label>
                        <label title="Item within the subfield — 1 unless the subfield repeats">
                          <span>Item</span>
                          <input className="input wf-pos-num" type="number" min={1} value={posOf(f).item}
                            onChange={(e) => setPos(i, { item: Number(e.target.value) })} />
                        </label>
                        <span className="field-help">
                          Written to <code>{refOf(f)}</code>
                          {posOf(f).subfield === 1 && posOf(f).item === 1 ? ' — the whole field' : ''}
                        </span>
                      </div>
                    )}
                    {specLoading && <span className="field-help">reading the specification…</span>}
                    {specError && <span className="cart-error" style={{ margin: 0 }}>{specError}</span>}
                    {sf && (
                      <span className="field-help">
                        {sf.characterType} {sf.minLength === sf.maxLength ? sf.minLength : `${sf.minLength}–${sf.maxLength}`}
                        {sf.condition ? ` · ${sf.condition === 'M' ? 'mandatory' : 'optional'}` : ''}
                        {sf.example ? ` · e.g. ${sf.example}` : ''}
                      </span>
                    )}
                  </div>
                )}

                {/* An EBTS-mapped select: the permitted values, tick to offer. */}
                {mapped && sf?.values?.length ? (() => {
                  const chosen = offered(f, sf);
                  return (
                    <div className="wf-field-values">
                      <span className="field-help">
                        {sf.valuesMandatory
                          ? 'The specification REQUIRES one of these. Untick any this workflow should not offer.'
                          : 'Values the specification lists. Untick any this workflow should not offer.'}
                        {' '}{chosen.size} of {sf.values.length} offered.
                      </span>
                      <div className="wf-values-list">
                        {sf.values.map((v) => (
                          <label className="checkbox" key={v.code} title={v.code}>
                            <input type="checkbox" checked={chosen.has(v.code)} onChange={() => toggleValue(i, v.code)} />
                            <span><span className="mono">{v.code}</span> {v.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })() : null}

                {/*
                  * An unmapped select has no specification to draw values from, so the workflow supplies its
                  * own. Value and label kept apart: the value is what the file carries, the label is what the
                  * operator reads, and conflating them is how a code ends up displayed to a customer.
                  */}
                {!mapped && f.type === 'select' && (
                  <div className="wf-field-values">
                    <span className="field-help">
                      This dropdown is not mapped to the EBTS, so list its values here.
                      {!(f.options ?? []).length && ' It is empty and will show nothing on the form.'}
                    </span>
                    <div className="stack" style={{ gap: 4 }}>
                      {(f.options ?? []).map((o, oi) => (
                        <div className="inline-form" key={oi}>
                          <input className="input mono" style={{ maxWidth: 140 }} value={o.value} placeholder="value"
                            onChange={(e) => setOwnOption(i, oi, { value: e.target.value })} />
                          <input className="input" value={o.label} placeholder="label shown to the operator"
                            onChange={(e) => setOwnOption(i, oi, { label: e.target.value })} />
                          <button className="btn btn-ghost btn-sm" title="Remove value"
                            onClick={() => upd(i, { options: (f.options ?? []).filter((_, x) => x !== oi) })}>✕</button>
                        </div>
                      ))}
                      <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
                        onClick={() => upd(i, { options: [...(f.options ?? []), { value: '', label: '' }] })}>+ Add value</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        onClick={() => { onChange([...fields, { name: '', label: '', type: 'text' }]); setOpen(fields.length); }}>
        + Add field
      </button>
    </div>
  );
}

/**
 * The single static-field list, composed from whichever shape a configuration holds.
 *
 * Mirrors services/lib/domain/staticFields.ts, which is what derives the two record maps on save. The keys
 * in those maps are bare WITHIN their record, so the record comes from which map they were in — reading `5`
 * from the Type-2 map as anything but 2.005 would move a constant into a different record.
 */
function mergeStatics(cfg: WorkflowConfig): Record<string, string> {
  if (cfg.staticFields && Object.keys(cfg.staticFields).length) return { ...cfg.staticFields };
  const out: Record<string, string> = {};
  for (const [record, map] of [[1, cfg.staticType1Fields], [2, cfg.staticType2Fields]] as const) {
    for (const [key, value] of Object.entries(map ?? {})) {
      const [field = key, ...rest] = key.split('.');
      const suffix = rest.length ? `.${rest.join('.')}` : '';
      out[`${record}.${String(field).padStart(3, '0')}${suffix}`] = value;
    }
  }
  return out;
}

/**
 * Static fields: pick the field from the specification, type the value.
 *
 * Two tables keyed by bare field numbers became one, because "which record" is a property of the field and
 * not a question the person setting a constant should have to answer.
 *
 * Rows are held as an ARRAY for the life of the editor: projecting them through an object on every keystroke
 * reorders integer-like keys numerically, which made the list jump under the cursor in the editor this
 * replaces.
 */
function StaticFieldsEditor({ statics, onChange, specFields, specLoading }: {
  statics: Record<string, string>;
  onChange: (r: Record<string, string>) => void;
  specFields: SpecField[];
  specLoading: boolean;
}) {
  const [rows, setRows] = useState<[string, string][]>(() => Object.entries(statics));
  // Only the records the builder writes. Offering a Type-10 field would store a constant nothing reads.
  const writable = specFields.filter((sf) => sf.record === 1 || sf.record === 2);
  const byRef = new Map(writable.map((sf) => [sf.ref, sf]));

  const commit = (next: [string, string][]) => {
    setRows(next);
    onChange(Object.fromEntries(next.filter(([ref]) => ref.trim())));
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      {rows.map(([ref, value], i) => {
        const sf = byRef.get(displayRef(ref));
        return (
          <div className="inline-form" key={i}>
            <select className="select" style={{ maxWidth: 340 }} value={byRef.has(displayRef(ref)) ? displayRef(ref) : ref}
              onChange={(e) => commit(rows.map((r, idx) => (idx === i ? [e.target.value, r[1]] : r)))}>
              <option value="">— choose a field —</option>
              {/* A constant already set on a field this specification does not define stays listed, so
                  saving cannot quietly drop it. */}
              {ref && !byRef.has(displayRef(ref)) && <option value={ref}>{ref} (not in this specification)</option>}
              {writable.map((s) => (
                <option key={s.ref} value={s.ref}>
                  {s.ref} {s.mnemonic}{s.description ? ` — ${s.description}` : ''}{s.condition === 'M' ? ' *' : ''}
                </option>
              ))}
            </select>
            {/* A field with a value map gets a dropdown: a constant outside it is a violation on every
                transaction this workflow ever writes, which is the worst place to allow a typo. */}
            {sf?.values?.length ? (
              <select className="select" value={value}
                onChange={(e) => commit(rows.map((r, idx) => (idx === i ? [r[0], e.target.value] : r)))}>
                <option value="">— choose a value —</option>
                {sf.values.map((v) => <option key={v.code} value={v.code}>{v.code} — {v.label}</option>)}
              </select>
            ) : (
              <input className="input" value={value} placeholder={sf?.example ? `e.g. ${sf.example}` : 'value'}
                onChange={(e) => commit(rows.map((r, idx) => (idx === i ? [r[0], e.target.value] : r)))} />
            )}
            <button className="btn btn-ghost btn-sm" title="Remove" onClick={() => commit(rows.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        );
      })}
      {specLoading && <span className="field-help">reading the specification…</span>}
      <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        onClick={() => commit([...rows, ['', '']])}>+ Add static field</button>
    </div>
  );
}

/** The operations a rule can perform, in the order they are offered. */
const OP_KINDS: { op: RuleOp['op']; label: string }[] = [
  { op: 'upper', label: 'uppercase' },
  { op: 'lower', label: 'lowercase' },
  { op: 'trim', label: 'trim' },
  { op: 'append', label: 'append' },
  { op: 'prepend', label: 'prepend' },
  { op: 'padStart', label: 'pad to length (left)' },
  { op: 'padEnd', label: 'pad to length (right)' },
  { op: 'regex', label: 'regex replace' },
  { op: 'slice', label: 'slice' },
  { op: 'crlf', label: 'normalize line endings' },
  { op: 'write', label: 'write →' },
];

/** A sensible starting shape per operation, so adding one never produces an invalid rule. */
function blankOp(kind: RuleOp['op']): RuleOp {
  switch (kind) {
    case 'append': case 'prepend': return { op: kind, literal: '' };
    case 'padStart': return { op: 'padStart', length: 3, pad: '0' };
    case 'padEnd': return { op: 'padEnd', length: 3, pad: ' ' };
    case 'regex': return { op: 'regex', pattern: '', replace: '' };
    case 'slice': return { op: 'slice', start: 0 };
    case 'write': return { op: 'write', field: '' };
    default: return { op: kind } as RuleOp;
  }
}

/** A reference: a literal, another form field, or an EBTS field. One of the three, never two. */
function RefEditor({ value, onChange, fieldNames }: {
  value: RuleRef; onChange: (r: RuleRef) => void; fieldNames: string[];
}) {
  const kind = refKind(value);
  return (
    <>
      <select className="select" style={{ maxWidth: 110 }} value={kind}
        onChange={(e) => {
          const k = e.target.value;
          onChange(k === 'field' ? { field: '' } : k === 'ebts' ? { ebts: '' } : { literal: '' });
        }}>
        <option value="literal">text</option>
        <option value="field">form field</option>
        <option value="ebts">EBTS field</option>
      </select>
      {kind === 'field' ? (
        <input className="input mono" style={{ maxWidth: 190 }} list="wf-field-names" value={value.field ?? ''}
          placeholder="form id" onChange={(e) => onChange({ field: e.target.value })} />
      ) : kind === 'ebts' ? (
        <input className="input mono" style={{ maxWidth: 130 }} value={value.ebts ?? ''}
          placeholder="2.037" onChange={(e) => onChange({ ebts: e.target.value })} />
      ) : (
        <input className="input" value={value.literal ?? ''} placeholder="text"
          onChange={(e) => onChange({ literal: e.target.value })} />
      )}
      <datalist id="wf-field-names">{fieldNames.map((n) => <option key={n} value={n} />)}</datalist>
    </>
  );
}

/**
 * Post-processing rules: read a value, transform it, write it somewhere.
 *
 * Shown with a PREVIEW, and that is not a nicety. A rule engine that cannot be dry-run is one nobody will
 * touch — the cost of being wrong is a rejected FBI submission days later, so seeing the output against
 * sample data before saving is what makes the feature usable at all. The engine is a pure function, so the
 * preview runs the same code the capture will.
 */
export interface RuleGroup { groupId: string; name: string; description?: string; rules: WorkflowRule[] }

/**
 * Exported so the rules library edits a group with the SAME editor a workflow edits its rules with. A second
 * implementation would drift, and the two would disagree about what a rule can do.
 */
export function RulesEditor({ rules, onChange, fieldNames }: {
  rules: WorkflowRule[];
  onChange: (r: WorkflowRule[]) => void;
  fieldNames: string[];
}) {
  const isAdmin = useIsSiteAdmin();
  const library = useQuery({
    queryKey: ['rule-groups'],
    queryFn: () => api.get<RuleGroup[]>('/admin/rule-groups'),
    enabled: isAdmin,
    staleTime: 300_000,
  });
  const [open, setOpen] = useState<number | null>(null);
  /** Sample form data for the preview, as `name=value` lines — quicker to type than a form of inputs. */
  const [sample, setSample] = useState('');
  /*
   * Promoting rules into the library. Which rules is a CHOICE, not "all of them": the group that matters here
   * is the five name rules, and a workflow's other rules have nothing to do with them.
   */
  const [promoting, setPromoting] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const saveGroup = useMutation({
    mutationFn: (g: { groupId: string; name: string; description: string; rules: WorkflowRule[] }) =>
      api.put(`/admin/rule-groups/${encodeURIComponent(g.groupId)}`, { name: g.name, description: g.description, rules: g.rules }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rule-groups'] });
      setPromoting(false); setPicked(new Set()); setGroupName(''); setGroupDesc(''); setSaveErr(null);
    },
    onError: (e) => setSaveErr(e instanceof ApiError ? e.message : 'Could not save the group'),
  });

  const upd = (i: number, patch: Partial<WorkflowRule>) =>
    onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const updOp = (i: number, oi: number, op: RuleOp) =>
    upd(i, { ops: (rules[i]?.ops ?? []).map((o, x) => (x === oi ? op : o)) });
  const move = (i: number, to: number) => {
    if (to < 0 || to >= rules.length) return;
    const next = [...rules];
    const [item] = next.splice(i, 1);
    if (item) next.splice(to, 0, item);
    onChange(next);
    setOpen(to);
  };

  /* Parsed leniently: this is a scratchpad, and a half-typed line should not blank the whole preview. */
  const sampleValues = useMemo(() => {
    const out: Record<string, string> = {};
    for (const line of sample.split('\n')) {
      const eq = line.indexOf('=');
      if (eq <= 0) continue;
      out[line.slice(0, eq).trim()] = line.slice(eq + 1).trim();
    }
    return out;
  }, [sample]);

  /* Run against a COPY: the preview must not leave anything behind in the sample it was given. */
  const preview = useMemo(() => {
    const values = { ...sampleValues };
    let ebts: Record<string, string> = {};
    try {
      ebts = runRules(rules, values);
    } catch (err) {
      return { values, ebts, error: err instanceof Error ? err.message : 'The rules could not be run' };
    }
    const written = Object.keys(values).filter((k) => values[k] !== sampleValues[k]);
    return { values, ebts, written, error: null as string | null };
  }, [rules, sampleValues]);

  return (
    <div className="stack" style={{ gap: 8 }}>
      {/*
        * Copied BY VALUE. What lands here is this workflow's own rules, editable freely; the library entry is
        * untouched. A reference would mean editing a library entry silently rewrites how every workflow using
        * it builds its EFTs, and a rule changing under a workflow's feet is the last thing to introduce here.
        */}
      {(library.data?.length ?? 0) > 0 && (
        <div className="wf-inline">
          <span className="wf-oplabel">library</span>
          <select className="select" style={{ maxWidth: 300 }} value=""
            onChange={(e) => {
              const g = library.data?.find((x) => x.groupId === e.target.value);
              if (!g) return;
              // Deep copy: sharing the ops arrays would make a local edit rewrite the library entry.
              const copied = JSON.parse(JSON.stringify(g.rules ?? [])) as WorkflowRule[];
              onChange([...rules, ...copied]);
              setOpen(null);
            }}>
            <option value="">+ Add rules from the library…</option>
            {library.data?.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.name} ({g.rules?.length ?? 0} rule{(g.rules?.length ?? 0) === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          <span className="field-help">Copied in and editable here; the library keeps its own version.</span>
        </div>
      )}

      {rules.length === 0 && (
        <p className="cart-note" style={{ textAlign: 'left', margin: 0 }}>
          // no rules — the transaction is built from the field mappings and static fields alone
        </p>
      )}

      {rules.map((r, i) => {
        const isOpen = open === i;
        return (
          <div className={`wf-row${r.enabled === false ? ' is-off' : ''}`} key={i}>
            <div className="wf-head">
              <button type="button" className="wf-disclose" aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : i)}>
                <span className="wf-caret" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                <span className="wf-id">{r.label || <em>unnamed rule</em>}</span>
                <span className="wf-rule mono">{ruleText(r)}</span>
                {whenText(r) && <span className="wf-when">{whenText(r)}</span>}
                {r.enabled === false && <span className="wf-type">off</span>}
              </button>
              <button className="btn btn-ghost btn-sm wf-move" disabled={i === 0} title="Move up"
                onClick={() => move(i, i - 1)}>↑</button>
              <button className="btn btn-ghost btn-sm wf-move" disabled={i === rules.length - 1} title="Move down"
                onClick={() => move(i, i + 1)}>↓</button>
              <button className="btn btn-ghost btn-sm" title="Remove rule"
                onClick={() => { onChange(rules.filter((_, x) => x !== i)); setOpen(null); }}>✕</button>
            </div>

            {isOpen && (
              <div className="wf-body">
                <div className="wf-inline">
                  <input className="input" style={{ maxWidth: 260 }} value={r.label ?? ''} placeholder="what this rule is for"
                    onChange={(e) => upd(i, { label: e.target.value })} />
                  <label className="checkbox">
                    <input type="checkbox" checked={r.enabled !== false}
                      onChange={(e) => upd(i, { enabled: e.target.checked })} /><span>Enabled</span>
                  </label>
                </div>

                {/* The condition. Without one, a rule that produces nothing writes an empty field, which the
                    FBI rejects as a violation rather than ignoring. */}
                <div className="wf-inline">
                  <span className="wf-oplabel">only when</span>
                  <select className="select" style={{ maxWidth: 120 }} value={r.when ? (r.when.ebts !== undefined ? 'ebts' : 'field') : ''}
                    onChange={(e) => {
                      const k = e.target.value;
                      upd(i, k === '' ? { when: undefined } : { when: k === 'ebts' ? { ebts: '' } : { field: '' } });
                    }}>
                    <option value="">always</option>
                    <option value="field">form field</option>
                    <option value="ebts">EBTS field</option>
                  </select>
                  {r.when && (
                    <>
                      <input className="input mono" style={{ maxWidth: 170 }}
                        list={r.when.ebts !== undefined ? undefined : 'wf-field-names'}
                        value={r.when.field ?? r.when.ebts ?? ''}
                        placeholder={r.when.ebts !== undefined ? '2.037' : 'form id'}
                        onChange={(e) => upd(i, { when: { ...r.when, ...(r.when?.ebts !== undefined ? { ebts: e.target.value } : { field: e.target.value }) } })} />
                      <select className="select" style={{ maxWidth: 150 }} value={r.when.is ?? 'notEmpty'}
                        onChange={(e) => upd(i, { when: { ...r.when, is: e.target.value as NonNullable<WorkflowRule['when']>['is'] } })}>
                        <option value="notEmpty">has a value</option>
                        <option value="empty">is empty</option>
                        <option value="equals">equals</option>
                        <option value="matches">matches</option>
                      </select>
                      {(r.when.is === 'equals' || r.when.is === 'matches') && (
                        <input className="input" style={{ maxWidth: 170 }} value={r.when.value ?? ''} placeholder="value"
                          onChange={(e) => upd(i, { when: { ...r.when, value: e.target.value } })} />
                      )}
                      <label className="checkbox"><input type="checkbox" checked={!!r.when.not}
                        onChange={(e) => upd(i, { when: { ...r.when, not: e.target.checked || undefined } })} /><span>not</span></label>
                    </>
                  )}
                </div>

                <div className="wf-inline">
                  <span className="wf-oplabel">read</span>
                  <RefEditor value={r.read} onChange={(read) => upd(i, { read })} fieldNames={fieldNames} />
                </div>

                {(r.ops ?? []).map((op, oi) => (
                  <div className="wf-inline wf-opline" key={oi}>
                    <span className="wf-oplabel">then</span>
                    <select className="select" style={{ maxWidth: 190 }} value={op.op}
                      onChange={(e) => updOp(i, oi, blankOp(e.target.value as RuleOp['op']))}>
                      {OP_KINDS.map((k) => <option key={k.op} value={k.op}>{k.label}</option>)}
                    </select>

                    {(op.op === 'append' || op.op === 'prepend') && (
                      <RefEditor value={op} onChange={(ref) => updOp(i, oi, { op: op.op, ...ref })} fieldNames={fieldNames} />
                    )}
                    {(op.op === 'padStart' || op.op === 'padEnd') && (
                      <>
                        <input className="input" type="number" style={{ maxWidth: 90 }} value={op.length}
                          onChange={(e) => updOp(i, oi, { ...op, length: Number(e.target.value) })} />
                        <input className="input mono" style={{ maxWidth: 70 }} value={op.pad ?? ''} placeholder="pad"
                          onChange={(e) => updOp(i, oi, { ...op, pad: e.target.value })} />
                      </>
                    )}
                    {op.op === 'slice' && (
                      <>
                        <input className="input" type="number" style={{ maxWidth: 90 }} value={op.start}
                          onChange={(e) => updOp(i, oi, { ...op, start: Number(e.target.value) })} />
                        <input className="input" type="number" style={{ maxWidth: 90 }} value={op.end ?? ''} placeholder="end"
                          onChange={(e) => updOp(i, oi, { ...op, end: e.target.value === '' ? undefined : Number(e.target.value) })} />
                      </>
                    )}
                    {op.op === 'regex' && (
                      <>
                        <input className="input mono" style={{ maxWidth: 170 }} value={op.pattern} placeholder="pattern"
                          onChange={(e) => updOp(i, oi, { ...op, pattern: e.target.value })} />
                        <input className="input mono" style={{ maxWidth: 150 }} value={op.replace} placeholder="replacement"
                          onChange={(e) => updOp(i, oi, { ...op, replace: e.target.value })} />
                      </>
                    )}
                    {op.op === 'write' && (
                      <>
                        <select className="select" style={{ maxWidth: 130 }} value={op.ebts !== undefined ? 'ebts' : 'field'}
                          onChange={(e) => updOp(i, oi, e.target.value === 'ebts' ? { op: 'write', ebts: '' } : { op: 'write', field: '' })}>
                          <option value="field">form field</option>
                          <option value="ebts">EBTS field</option>
                        </select>
                        <input className="input mono" style={{ maxWidth: 190 }}
                          list={op.ebts !== undefined ? undefined : 'wf-field-names'}
                          value={op.ebts ?? op.field ?? ''}
                          placeholder={op.ebts !== undefined ? '2.018' : 'form id'}
                          onChange={(e) => updOp(i, oi, op.ebts !== undefined ? { op: 'write', ebts: e.target.value } : { op: 'write', field: e.target.value })} />
                      </>
                    )}

                    <button className="btn btn-ghost btn-sm" title="Remove step"
                      onClick={() => upd(i, { ops: (r.ops ?? []).filter((_, x) => x !== oi) })}>✕</button>
                  </div>
                ))}

                <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
                  onClick={() => upd(i, { ops: [...(r.ops ?? []), blankOp('upper')] })}>+ Add step</button>
              </div>
            )}
          </div>
        );
      })}

      <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        onClick={() => { onChange([...rules, { label: '', read: { field: '' }, ops: [] }]); setOpen(rules.length); }}>
        + Add rule
      </button>

      {/*
        * Promote to the library. This is how a group should come into being: built on a real workflow, proved
        * with the preview, then saved — rather than authored blind in a library screen.
        */}
      {rules.length > 0 && !promoting && (
        <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
          onClick={() => { setPromoting(true); setPicked(new Set(rules.map((_, i) => i))); }}>
          Save rules to the library…
        </button>
      )}
      {promoting && (
        <div className="wf-promote">
          <div className="section-title" style={{ marginTop: 0 }}>Save to the library</div>
          <p className="field-help" style={{ margin: '0 0 8px' }}>
            Copied as they are now. Editing them here afterwards will not change the group, and editing the
            group will not change this workflow.
          </p>
          <div className="stack" style={{ gap: 4 }}>
            {rules.map((r, i) => (
              <label className="checkbox" key={i}>
                <input type="checkbox" checked={picked.has(i)} onChange={() => {
                  const next = new Set(picked);
                  if (next.has(i)) next.delete(i); else next.add(i);
                  setPicked(next);
                }} />
                <span>{r.label || <em>unnamed</em>} <span className="wf-rule mono">{ruleText(r)}</span></span>
              </label>
            ))}
          </div>
          <div className="wf-grid" style={{ marginTop: 8 }}>
            <label className="field">
              <span className="field-label">Group name</span>
              <input className="input" value={groupName} placeholder="Format Name"
                onChange={(e) => setGroupName(e.target.value)} />
            </label>
            <label className="field" style={{ gridColumn: '2 / -1' }}>
              <span className="field-label">Description</span>
              <input className="input" value={groupDesc} placeholder="What it does, and anything a newcomer would not guess."
                onChange={(e) => setGroupDesc(e.target.value)} />
            </label>
          </div>
          {saveErr && <p className="cart-error">{saveErr}</p>}
          <div className="wf-inline" style={{ marginTop: 6 }}>
            <button className="btn btn-primary btn-sm"
              disabled={!groupName.trim() || picked.size === 0 || saveGroup.isPending}
              onClick={() => saveGroup.mutate({
                /* The id is derived from the name, which is what an admin actually chose. */
                groupId: groupName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                name: groupName.trim(),
                description: groupDesc.trim(),
                // Deep copy, in the ORDER they appear — these rules depend on running in sequence.
                rules: JSON.parse(JSON.stringify(rules.filter((_, i) => picked.has(i)))) as WorkflowRule[],
              })}>
              {saveGroup.isPending ? 'Saving…' : `Save ${picked.size} rule${picked.size === 1 ? '' : 's'}`}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setPromoting(false); setSaveErr(null); }}>Cancel</button>
            <span className="field-help">Saving under an existing name replaces that group.</span>
          </div>
        </div>
      )}

      {/*
        * The preview. The cost of a wrong rule is a rejected submission days later, so the point is to be
        * wrong here instead — against sample data, running the same engine the capture will.
        */}
      {rules.length > 0 && (
        <div className="wf-preview">
          <div className="section-title" style={{ marginTop: 0 }}>Preview</div>
          <p className="field-help" style={{ margin: '0 0 6px' }}>
            Sample form data, one <span className="mono">name=value</span> per line. The rules run against it
            exactly as they will during a capture.
          </p>
          <textarea className="input field-multiline" rows={4} value={sample}
            placeholder={fieldNames.slice(0, 3).map((n) => `${n}=`).join('\n') || 'lastName=Johnson\nfirstName=Adam'}
            onChange={(e) => setSample(e.target.value)} />
          {preview.error ? <p className="cart-error">{preview.error}</p> : (
            <div className="wf-preview-out">
              <div>
                <div className="field-label">Form data written</div>
                {(preview.written ?? []).length === 0
                  ? <span className="field-help">— nothing —</span>
                  : (preview.written ?? []).map((k) => (
                    <div key={k} className="wf-preview-row"><span className="mono">{k}</span><strong className="mono">{preview.values[k]}</strong></div>
                  ))}
              </div>
              <div>
                <div className="field-label">EBTS fields written</div>
                {Object.keys(preview.ebts).length === 0
                  ? <span className="field-help">— nothing —</span>
                  : Object.entries(preview.ebts).map(([k, v]) => (
                    <div key={k} className="wf-preview-row"><span className="mono">{k}</span><strong className="mono">{v}</strong></div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
